import React from "react";
import { gql } from "@apollo/client";
import { Skeleton } from "@src/lib/ui/Skeleton";
import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";
import { useGuestFeedbackThemeSummaryQuery } from "./TopThemesSidebar.generated";
import { useRecommendationsMockData } from "@src/features/recommendations/RecommendationsMockDataContext";
import { getMockThemeSummary } from "./recommendationsMockData";

const TOP_THEMES_LIMIT = 10;

type Props = {
  selectedThemeId: string | null | undefined;
  onSelectTheme: (themeId: string | null) => void;
  search?: string;
  startDate?: Date;
  endDate?: Date;
  minRating?: number;
  maxRating?: number;
  locationIds?: number[];
};

const TopThemesRow: React.FC<Props> = ({
  selectedThemeId,
  onSelectTheme,
  search,
  startDate,
  endDate,
  minRating,
  maxRating,
  locationIds,
}) => {
  const { useMockData } = useRecommendationsMockData();
  const { data, loading: queryLoading } = useGuestFeedbackThemeSummaryQuery({
    variables: {
      input: {
        limit: TOP_THEMES_LIMIT,
        search: search ? { value: search } : undefined,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        minRating,
        maxRating,
        locationIds: locationIds?.length ? locationIds : undefined,
      },
    },
    skip: useMockData,
  });

  const loading = queryLoading && !useMockData;
  const topThemes = useMockData
    ? getMockThemeSummary({
        limit: TOP_THEMES_LIMIT,
        search,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        minRating,
        maxRating,
        locationIds,
      })
    : data?.GuestFeedbackThemeSummary ?? [];

  if (loading) {
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Text
          variant="fine-print-description"
          className="mr-1 text-muted-foreground"
        >
          Top themes
        </Text>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-6 w-32 rounded-md" />
        ))}
      </div>
    );
  }

  if (topThemes.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <Text
        variant="fine-print-description"
        className="mr-1 text-muted-foreground"
      >
        Top themes
      </Text>
      {topThemes.map((theme) => {
        const isActive = theme.id === selectedThemeId;
        return (
          <button
            key={theme.id}
            type="button"
            aria-pressed={isActive}
            onClick={() => onSelectTheme(isActive ? null : theme.id)}
            className={cn(
              "inline-flex h-6 items-center rounded-md border px-2 text-xs transition-colors",
              isActive
                ? "border-gray-12 bg-gray-12 text-background"
                : "border-gray-3 bg-background text-foreground hover:bg-gray-1",
            )}
          >
            {theme.name}
            <span
              className={cn(
                "ml-1 tabular-nums",
                isActive ? "text-background/70" : "text-muted-foreground",
              )}
            >
              {theme.mentionCount ?? 0}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default TopThemesRow;

gql`
  query GuestFeedbackThemeSummary($input: GuestFeedbackThemeSummaryInput) {
    GuestFeedbackThemeSummary(input: $input) {
      id
      name
      mentionCount
    }
  }
`;
